import React, { useEffect, useState } from 'react';

import { useOnboardingStatus } from '../../util/onboardingStatusStore';
import {
  isOnboardingGatedRoute,
  onboardingRedirectFor,
  ONBOARDING_STEP_INFO,
  STEP_EMAIL,
} from '../../util/onboardingSteps';
import NamedRedirect from '../NamedRedirect/NamedRedirect';
import NamedLink from '../NamedLink/NamedLink';

import { OnboardingCelebration } from '../OnboardingCheer/OnboardingCheer';
import {
  onboardingCheer,
  markOnboardingActive,
  markCelebrated,
  shouldCelebrate,
} from '../../util/onboardingCheer';

import css from './OnboardingRouteGate.module.css';

/**
 * Strict onboarding gate around every signed-in page (Sam, Oct 7 2026).
 * - Otherwise, a page that belongs to a later step → /onboarding.
 * - On an allowed step page, a slim bar (warm line + step title) links back to setup.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.route
 * @param {Object} props.location
 * @param {Object} [props.currentUser]
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
const PAGES_WITH_OWN_CHEER = new Set(['StripePayoutPage', 'EditListingPage']);

const OnboardingRouteGate = props => {
  const { route, location, currentUser, children } = props;
  const userId = currentUser?.id?.uuid || null;
  const gated = !!userId && isOnboardingGatedRoute(route);
  const unverified = false; // email is the last step now; status decides
  const { status } = useOnboardingStatus(userId, location?.pathname || '', {
    enabled: gated && !unverified,
  });

  const [celebrate, setCelebrate] = useState(false);
  // Sam 2:08 Oct 23: dismissible for this browser session.
  const [barHidden, setBarHidden] = useState(() => {
    try {
      return typeof window !== 'undefined' && window.sessionStorage.getItem('SetupBarHidden') === '1';
    } catch (e) {
      return false;
    }
  });
  const hideBar = () => {
    setBarHidden(true);
    try {
      window.sessionStorage.setItem('SetupBarHidden', '1');
    } catch (e) {
      // private mode
    }
  };
  // Sam 9:29 PM Oct 23: signed up from a listing's Talk Now / Book → back to that step.
  useEffect(() => {
    if (!userId || typeof window === 'undefined') return;
    const bi = require('../../util/bookingIntent');
    const resume = bi.peekBookResume();
    if (!resume) return;
    const here = window.location.pathname + window.location.search;
    if (/\/order\/|\/checkout/.test(here)) {
      bi.clearBookResume();
      return;
    }
    if (here === resume) return;
    const base = resume.split('?')[0].replace(/\/(book|checkout)$/, '');
    if (window.location.pathname === base) {
      window.location.replace(resume);
    }
  }, [userId, location?.pathname]);
  const allDone = !!status?.allDone;
  const current = status?.current || null;
  useEffect(() => {
    if (!gated || unverified || !userId || !status) {
      return;
    }
    if (allDone) {
      // Finished setup on this device → one-time celebration wherever they land.
      if (shouldCelebrate(userId) && route?.name !== 'OnboardingPage') {
        setCelebrate(true);
      }
    } else if (current) {
      markOnboardingActive(userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gated, unverified, userId, allDone, current]);
  const closeCelebration = () => {
    markCelebrated(userId);
    setCelebrate(false);
  };

  if (!gated) {
    return children;
  }
  const redirect = onboardingRedirectFor({ route, currentUser, status });
  if (redirect) {
    return <NamedRedirect name={redirect.name} search={redirect.search} />;
  }

  // Sam 2:31 AM Oct 24: setup prompts live on the dashboard only, never site-wide.
  const showBar =
    false &&
    !unverified &&
    status &&
    !status.allDone &&
    status.current &&
    status.current !== STEP_EMAIL &&
    route.name !== 'OnboardingPage' &&
    // Sam 2:11 Oct 23: don't push card/ID on talkers who are set for free Talk Now.
    // Sam 2:14: talkers have no profile step, so never nag about profile/About either.
    !(status.role === 'talk' && (status.current === 'talkProfile' || status.current === 'card' || status.current === 'identity' || (status.current === 'trust' && require('../../util/adultSelfAttest').isAdultSelfAttestedUser(currentUser)))) &&
    route.name !== 'EmailVerificationPage' &&
    // Sam 5:30 Oct 23: the bar covered Save schedule / Next in the profile wizard at 390px.
    route.name !== 'EditListingPage' &&
    // Sam 11:30 PM Oct 23: never nudge into earner setup while booking or in a session.
    !['CheckoutPage', 'OrderDetailsPage', 'OrderDetailsPageRedirect', 'ListingBookingPage', 'ListingPage', 'ListingPageCanonical', 'TransactionPage'].includes(route.name) &&
    // Dashboard shows the full Finish your setup checklist instead (Oct 16).
    route.name !== 'DashboardPage';
  if (!showBar || barHidden) {
    if (celebrate && allDone) {
      return (
        <>
          {children}
          <OnboardingCelebration role={status.role} asOverlay onAction={closeCelebration} />
        </>
      );
    }
    return children;
  }
  const info = ONBOARDING_STEP_INFO[status.current] || {};
  // Payouts / profile pages show their own cheer line at the top; don't repeat it
  // in the bottom bar (it showed twice on payouts).
  const pageHasOwnCheer = PAGES_WITH_OWN_CHEER.has(route.name);
  const cheer = pageHasOwnCheer
    ? null
    : onboardingCheer({ steps: status.steps, current: status.current, role: status.role });
  return (
    <>
      {children}
      <div className={css.bar} role="status" data-testid="onboarding-step-bar">
        <style>{`@media (max-width:767px){body{padding-bottom:128px !important}}`}</style>
        <span className={css.barText}>
          {cheer ? <strong className={css.barCheer}>{cheer.text}</strong> : null}{' '}
          {info.title || 'Finish setting up'}
        </span>
        <NamedLink name="OnboardingPage" className={css.barLink}>
          Continue setup
        </NamedLink>
        <button type="button" className={css.barClose} onClick={hideBar} aria-label="Hide setup reminder" data-testid="onboarding-step-bar-close">
          ✕
        </button>
      </div>
    </>
  );
};

export default OnboardingRouteGate;
